import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, OnInit, OnDestroy } from '@angular/core';
import { PopoverController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { RepStatusOptionWithColor } from '../profile-popup/profile-popup.component';
import { DeviceService } from '@omni/services/device/device.service';
import { NotificationService, ToastStyle } from '@omni/services/notification/notification.service';
import { REP_STATUS } from '@omni/models/rep-status-model';
import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'user-status-popover',
  templateUrl: './user-status-popover.component.html',
  styleUrl: './user-status-popover.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserStatusPopoverComponent implements OnInit, OnDestroy {
  @Input() selectedStatus: string = '';
  @Input() userStatuses$: Observable<RepStatusOptionWithColor[]> = new Observable();
  @Output() statusSelected = new EventEmitter<string>();

  public statuses: RepStatusOptionWithColor[] = [];
  private readonly destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(private readonly popoverCtrl: PopoverController,
    private readonly device: DeviceService,
    private readonly notificationService: NotificationService,
    private readonly translate: TranslateService
  ) { }

  ngOnInit() {
    // Subscribe to user status updates from the parent component
    if (this.userStatuses$) {
      this.userStatuses$.pipe(
        takeUntil(this.destroy$)
      ).subscribe(updatedStatuses => {
        this.statuses = updatedStatuses;
      });
    }

    if (this.device.isOffline) {
      if (this.device.isDeviceRealOffline) {
        this.statuses.forEach((status) => status.disabled = true);
      }
    }
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  trackByUserState(index: number, status: RepStatusOptionWithColor): string {
    return status?.userState ?? index.toString();
  }

  selectStatus(status: string) {
    const selected = this.statuses.find(s => s.userState === status);
    if (selected?.disabled) {
      // When the device has truly lost network connectivity, all statuses are
      // disabled (see profile-popup._setStatus). If the user tries to switch to
      // any non-OFFLINE status, show a clear toast explaining why it cannot be
      // updated — otherwise the click silently no-ops and leaves the user confused.
      if (this.device.isNativeApp && this.device.isDeviceRealOffline &&
          status !== REP_STATUS.OFFLINE.userState) {
        this.notificationService.notify(
          this.translate.instant('STATUS_NOT_UPDATED_NETWORK_ISSUE'),
          'menu-toggle',
          'top',
          ToastStyle.DANGER
        );
        this.popoverCtrl.dismiss();
      }
      return;
    }
    this.statusSelected.emit(status);
    this.popoverCtrl.dismiss(status);
  }
}
